import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Editor from '@monaco-editor/react';
import { useToast } from '@/lib/use-toast';
import p5 from 'p5';
import { findErrorLine, setupMonacoWithP5Types, createCustomConsole, createErrorHandlingWrapper, setupAssetPathRedirection } from '@/components/EditorUtils';
import { useTheme } from '@/lib/theme-provider';
import { Image } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import AssetManager from './AssetManager';

const EditorHalf = ({ 
  initialCode, 
  workdir,
  projectId,
  showPlay = true,
  showAssets = false, // Nouveau paramètre pour afficher le bouton Assets
  allowRun = true,
  autoFocus = true, // Option pour donner automatiquement le focus à l'éditeur
  onCodeChange,
}) => {
  const [code, setCode] = useState(initialCode || '');
  const [showCanvas, setShowCanvas] = useState(false);
  const [showAssetsManager, setShowAssetsManager] = useState(false); // État pour la modale Assets
  const [logs, setLogs] = useState([]);
  const canvasRef = useRef(null);
  const sketchInstance = useRef(null);
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const errorDecorations = useRef([]);
  const errorRef = useRef(false); // Référence pour suivre si une erreur a été détectée
  const { toast } = useToast();
  const { theme } = useTheme();

  // Charger le code depuis localStorage au chargement du composant si storageKey est fournie
  useEffect(() => {
    if (projectId) {
      const savedCode = localStorage.getItem(projectId);
      if (savedCode) {
        setCode(savedCode);
      }
    }
  }, [projectId]);

  // Mettre à jour le code quand initialCode change
  useEffect(() => {
    if (initialCode && initialCode !== code) {
      setCode(initialCode);
    }
  }, [initialCode]);


  // Fonction pour configurer Monaco avec les typages p5.js
  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    
    // Utiliser la fonction partagée pour configurer Monaco
    setupMonacoWithP5Types(editor, monaco);
    
    // Supprimer l'action de blocage du copier-coller si elle existe
    try {
      const action = editor.getAction('block-paste');
      if (action) {
        editor.removeAction('block-paste');
      }
    } catch (e) {
      // Ignorer si l'action n'existe pas
    }
    
    // Donner le focus à l'éditeur si autoFocus est activé
    if (autoFocus) {
      setTimeout(() => {
        editor.focus();
      }, 500); // Petit délai pour s'assurer que l'éditeur est complètement monté
    }
  };

  // Fonction pour ajouter une décoration d'erreur sur la ligne
  const highlightErrorLine = (lineNumber) => {
    if (!editorRef.current || !monacoRef.current || !lineNumber) return;
    
    // Supprimer les décorations existantes
    if (errorDecorations.current.length > 0) {
      errorDecorations.current = editorRef.current.deltaDecorations(errorDecorations.current, []);
    }
    
    // Ajouter la nouvelle décoration
    errorDecorations.current = editorRef.current.deltaDecorations([], [
      {
        range: new monacoRef.current.Range(lineNumber, 1, lineNumber, 1),
        options: {
          isWholeLine: true,
          className: 'myErrorLine'
        }
      }
    ]);
  };

  // Fonction pour effacer les décorations d'erreur
  const clearErrorHighlights = () => {
    if (editorRef.current && errorDecorations.current.length > 0) {
      errorDecorations.current = editorRef.current.deltaDecorations(errorDecorations.current, []);
    }
  };

  const showErrorInConsole = (errorLine, errorMessage, stackTrace, p) => {
    // Ne pas ajouter de message si une erreur est déjà signalée
    if (errorRef.current) {
      return;
    }
    
    // Marquer qu'une erreur a été signalée
    errorRef.current = true;

    let message = errorLine 
      ? `Erreur ligne ${errorLine}: ${errorMessage}`
      : `Erreur: ${errorMessage}`;

    console.log(stackTrace);

    // Mettre en surbrillance la ligne d'erreur dans l'éditeur
    highlightErrorLine(errorLine);

    // Ajouter l'erreur et les noms de fonctions aux logs
    setLogs(prevLogs => {
      const newLogs = [...prevLogs, message];
      return newLogs;
    });
    
    // Nettoyage complet des canvas sans réinitialiser les logs
    cleanupSketch();
    
    // Maintenant créer un nouveau canvas avec l'erreur
    setTimeout(() => {
      // Vérifier une dernière fois qu'il n'y a pas de canvas avant d'en créer un nouveau
      document.querySelectorAll('canvas[id^="defaultCanvas"]').forEach(canvas => {
        if (canvas && canvas.parentNode) {
          canvas.parentNode.removeChild(canvas);
        }
      });
      
      const errorSketch = (p) => {
        p.setup = function() {
          p.createCanvas(600, 600);
          p.background(50);
          p.fill(255);
          p.textSize(16);
          p.textAlign(p.CENTER, p.CENTER);
          p.text(message, p.width/2, p.height/2);
        }
        p.draw = function() {} // Fonction vide
        p.noLoop(); // Important: arrêter la boucle draw
      };
      
      sketchInstance.current = new p5(errorSketch, canvasRef.current);
    }, 0);
  };
  

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupSketch();
    };
  }, []);

  // Gérer le blocage du scroll quand la modal est ouverte
  useEffect(() => {
    if (showCanvas) {
      // Bloquer le scroll du body
      document.body.style.overflow = 'hidden';
    } else {
      // Restaurer le scroll du body
      document.body.style.overflow = 'unset';
    }

    // Cleanup au démontage du composant
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showCanvas]);

  const cleanupSketch = () => {
    if (sketchInstance.current) {
      try {
        // Nettoyer explicitement tous les event handlers
        const p = sketchInstance.current;
        
        // Supprimer les fonctions d'événements pour éviter qu'elles continuent à s'exécuter
        p.mousePressed = function() {};
        p.mouseReleased = function() {};
        p.mouseMoved = function() {};
        p.keyPressed = function() {};
        p.keyReleased = function() {};
        p.draw = function() {}; // Arrêter la boucle draw
        
        // Arrêter la boucle si elle tourne encore
        if (typeof p.noLoop === 'function') {
          p.noLoop();
        }
        
        // Supprimer complètement l'instance
        sketchInstance.current.remove();
      } catch (e) {
        console.error("Erreur lors du nettoyage du sketch:", e);
      }
      sketchInstance.current = null;

      if (canvasRef.current) {
        while (canvasRef.current.firstChild) {
          canvasRef.current.removeChild(canvasRef.current.firstChild);
        }
      }
    }
  };

  // Une fonction séparée pour réinitialiser les logs
  const resetLogs = () => {
    setLogs([]);
  };

  // Gestionnaire de changement de code pour effacer les erreurs
  const handleCodeChange = (newCode) => {
    setCode(newCode);
    // Effacer les décorations d'erreur quand l'utilisateur modifie le code
    clearErrorHighlights();
    
    // Sauvegarder le code dans localStorage si une clé est fournie
    if (projectId && newCode) {
      localStorage.setItem(projectId, newCode);
    }
    
    // Appeler le callback onCodeChange si fourni
    if (onCodeChange) {
      onCodeChange(newCode);
    }
  };

  const runSketch = () => {
    cleanupSketch();
    resetLogs(); // Réinitialiser les logs seulement au lancement d'un nouveau sketch
    setShowCanvas(true);
    errorRef.current = false; // Réinitialiser l'état d'erreur
    
    // Réinitialiser les décorations d'erreur précédentes
    clearErrorHighlights();

    const sketchFn = (p) => {
      try {
        const wrappedCode = `${code}\n//# sourceURL=userCode.js`;

        // Créer un objet console personnalisé pour capturer les logs
        const customConsole = createCustomConsole(log => {
          setLogs(prevLogs => [...prevLogs, log]);
        });

        // Configurer la redirection des chemins d'assets avant de créer les wrappers
        setupAssetPathRedirection(p, workdir, (errorLine, errorMessage, stack) => {
          showErrorInConsole(errorLine, errorMessage, stack, p);
        });

        const userFn = new Function('p', 'console', `
          with(p) {
            // Définir print comme alias de console.log
            const print = (...args) => console.log(...args);
            ${wrappedCode}
            return {
              preload: typeof preload === 'function' ? preload : null,
              setup: typeof setup === 'function' ? setup : null,
              draw: typeof draw === 'function' ? draw : null,
              mousePressed: typeof mousePressed === 'function' ? mousePressed : null,
              mouseReleased: typeof mouseReleased === 'function' ? mouseReleased : null,
              mouseMoved: typeof mouseMoved === 'function' ? mouseMoved : null,
              keyPressed: typeof keyPressed === 'function' ? keyPressed : null,
              keyReleased: typeof keyReleased === 'function' ? keyReleased : null
            };
          }
        `);

        const userFunctions = userFn(p, customConsole);

        // Callback d'erreur pour les wrappers
        const errorCallback = (errorLine, errorMessage, stack) => {
          showErrorInConsole(errorLine, errorMessage, stack, p);
        };

        // Appliquer les wrappers aux fonctions p5.js
        // Fonction preload - doit être appelée en premier pour charger les assets
        if (userFunctions.preload) {
          p.preload = createErrorHandlingWrapper(
            () => userFunctions.preload.call(p),
            "preload",
            errorCallback,
            false // Ne pas arrêter la loop en cas d'erreur dans preload
          );
        }

        p.setup = createErrorHandlingWrapper(
          function() {
            if (userFunctions.setup) {
              userFunctions.setup.call(p);
            } 
          }, 
          "setup",
          errorCallback
        );

        p.draw = createErrorHandlingWrapper(
          function() {
            if (userFunctions.draw) {
              userFunctions.draw.call(p);
            }
          }, 
          "draw", 
          errorCallback,
          true // Arrêter la loop en cas d'erreur
        );

        // Appliquer les wrappers aux autres fonctions d'événements si elles existent
        if (userFunctions.mousePressed) {
          p.mousePressed = createErrorHandlingWrapper(
            () => userFunctions.mousePressed.call(p),
            "mousePressed",
            errorCallback
          );
        }
        
        if (userFunctions.mouseReleased) {
          p.mouseReleased = createErrorHandlingWrapper(
            () => userFunctions.mouseReleased.call(p),
            "mouseReleased",
            errorCallback
          );
        }
        
        if (userFunctions.mouseMoved) {
          p.mouseMoved = createErrorHandlingWrapper(
            () => userFunctions.mouseMoved.call(p),
            "mouseMoved",
            errorCallback
          );
        }
        
        if (userFunctions.keyPressed) {
          p.keyPressed = createErrorHandlingWrapper(
            () => userFunctions.keyPressed.call(p),
            "keyPressed",
            errorCallback
          );
        }
        
        if (userFunctions.keyReleased) {
          p.keyReleased = createErrorHandlingWrapper(
            () => userFunctions.keyReleased.call(p),
            "keyReleased",
            errorCallback
          );
        }

      } catch (error) {
        console.error("Erreur:", error);
        const errorLineNumber = findErrorLine(code); // Utiliser la fonction partagée
        showErrorInConsole(errorLineNumber, error.message, error.stack, p);
        return false; // Ne pas relancer l'erreur
      }
    };

    setTimeout(() => {
      sketchInstance.current = new p5(sketchFn, canvasRef.current);
    }, 0);
  };

  // Système de redimensionnement adaptatif du canvas pour les cours
  useEffect(() => {
    const centerAndScaleCanvas = () => {
      if (canvasRef.current && showCanvas) {
        const canvases = canvasRef.current.querySelectorAll('canvas');
        if (canvases.length > 0) {
          const canvas = canvases[0];
          
          // Obtenir les dimensions originales du canvas
          const originalWidth = canvas.width;
          const originalHeight = canvas.height;
          
          if (originalWidth === 0 || originalHeight === 0) return; // Éviter la division par zéro
          
          // Calculer l'aspect ratio original
          const aspectRatio = originalWidth / originalHeight;
          
          // Obtenir les dimensions de la fenêtre
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          
          // Calculer l'espace disponible selon le contexte (mobile vs desktop)
          let availableWidth, availableHeight;
          
          if (viewportWidth < 768) { // Mobile
            // Sur mobile, utiliser plus d'espace avec moins de marges
            availableWidth = viewportWidth - 32; // 16px de marge de chaque côté
            availableHeight = viewportHeight * 0.5; // Maximum 50% de la hauteur
          } else { // Desktop
            // Sur desktop, utiliser tout l'espace disponible
            availableWidth = Math.min(viewportWidth * 0.85, 900) - 32; // Plus large avec moins de marge
            availableHeight = viewportHeight * 0.65; // Plus de hauteur disponible
          }
          
          // Calculer les dimensions en préservant l'aspect ratio
          let newWidth, newHeight;
          
          if (availableWidth / aspectRatio <= availableHeight) {
            // Limité par la largeur
            newWidth = Math.min(availableWidth, originalWidth);
            newHeight = newWidth / aspectRatio;
          } else {
            // Limité par la hauteur
            newHeight = Math.min(availableHeight, originalHeight);
            newWidth = newHeight * aspectRatio;
          }
          
          // S'assurer que les dimensions ne dépassent pas les originales
          if (newWidth > originalWidth || newHeight > originalHeight) {
            newWidth = originalWidth;
            newHeight = originalHeight;
          }
          
          // Arrondir les dimensions
          newWidth = Math.floor(newWidth);
          newHeight = Math.floor(newHeight);
          
          canvas.style.display = 'block';
          canvas.style.margin = 'auto';
          canvas.style.width = `${newWidth}px`;
          canvas.style.height = `${newHeight}px`;
          canvas.style.maxWidth = '100%';
          canvas.style.maxHeight = '100%';
          canvas.style.borderRadius = '8px';
          canvas.style.overflow = 'hidden';
          canvas.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
          
          //console.log(`Canvas cours redimensionné: ${originalWidth}x${originalHeight} → ${newWidth}x${newHeight} (ratio: ${aspectRatio.toFixed(2)})`);
        }
      }
    };

    // Observer le conteneur pour détecter quand un canvas est ajouté
    let observer;
    if (canvasRef.current) {
      observer = new MutationObserver(centerAndScaleCanvas);
      observer.observe(canvasRef.current, { childList: true, subtree: true });
    }

    // Appliquer le centrage et redimensionnement immédiatement si un canvas existe déjà
    centerAndScaleCanvas();
    
    // Écouter les changements de taille de la fenêtre pour redimensionner
    const handleResize = () => {
      setTimeout(centerAndScaleCanvas, 100); // Petit délai pour laisser le temps au navigateur de recalculer
    };
    
    window.addEventListener('resize', handleResize);

    return () => {
      if (observer) {
        observer.disconnect();
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [showCanvas]); // Dépendance sur showCanvas pour relancer quand le canvas est affiché

  return (
    <div className="h-full">
      {/* Éditeur */}
      <div className="h-full rounded-lg overflow-hidden">
        <Editor
          height="100%"
          defaultLanguage="javascript"
          value={code}
          onChange={handleCodeChange}
          onMount={handleEditorDidMount}
          theme={theme === 'dark' ? "vs-dark" : "vs"}
          options={{
            minimap: { enabled: false },
            fontSize: 16,
            fontFamily: "monospace",
            wordWrap: 'on',
            tabSize: 2,
            scrollBeyondLastLine: false,
            lineNumbers: 'on',
            folding: false,
            autoIndent: true,
            suggestOnTriggerCharacters: true,
            semanticHighlighting: true,
            overviewRulerLanes: 0,
            hideCursorInOverviewRuler: true,
            overviewRulerBorder: false,
            selectionHighlight: false,
            occurrencesHighlight: false,
            matchBrackets: 'never',
            renderLineHighlight: 'none',
            stickyScroll: {
              enabled: false
            }
          }}
        />

        {/* Boutons Play et Assets */}
        <div className="absolute top-3 right-6 z-30 flex flex-col gap-2">
          {/* Bouton Play - Affiché uniquement si showPlay est true */}
          {showPlay && (
            <button
              onClick={runSketch}
              className="bg-blue-500 hover:bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center shadow-md"
              title="Exécuter le code"
              disabled={!allowRun}
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                fill="currentColor" 
                className="w-5 h-5 ml-0.5"
              >
                <path d="M8 5.14v14l11-7-11-7z" />
              </svg>
            </button>
          )}

          {/* Bouton Assets - Affiché uniquement si showAssets est true */}
          {showAssets && (
            <button
              onClick={() => setShowAssetsManager(true)}
              className="bg-purple-500 hover:bg-purple-600 text-white rounded-full w-10 h-10 flex items-center justify-center shadow-md"
              title="Gérer les assets"
            >
              <Image className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Canvas Modal avec Console - Rendu via Portal */}
      {showCanvas && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
          style={{ zIndex: 99999 }}
          onClick={() => {
            cleanupSketch();
            setShowCanvas(false);
          }}
        >
          <div 
            className="bg-white dark:bg-neutral-800 rounded-lg p-1 md:p-3 flex flex-col gap-1 md:gap-2"
            onClick={e => e.stopPropagation()}
            style={{ 
              maxHeight: '98vh', // Utiliser toute la hauteur de l'écran
              maxWidth: '98vw',
              width: 'auto',
              height: 'auto'
            }}
          >
            {/* Canvas */}
            <div 
              ref={canvasRef} 
              className="bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center p-1"
              style={{
                maxWidth: '98vw',
                maxHeight: '80vh', // Utiliser toute la hauteur disponible
                overflow: 'hidden',
                minHeight: '200px'
              }}
            />
            
            {/* Console avec boutons par-dessus */}
            <div className="relative">
              {/* Console */}
              <div 
                className={`${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-neutral-100 text-gray-800'} rounded-lg p-3 font-mono text-sm overflow-y-auto`}
                style={{
                  width: '100%',
                  height: '150px',
                  minWidth: '400px'
                }}
                ref={(el) => {
                  if (el) {
                    el.scrollTop = el.scrollHeight;
                  }
                }}
              >
                <div className="font-bold mb-2">Console:</div>
                {logs.map((log, index) => (
                  <div key={index} className="whitespace-pre-wrap break-words">
                    {log}
                  </div>
                ))}
              </div>

              {/* Boutons de contrôle positionnés par-dessus en haut à droite */}
              <div className="absolute top-2 right-2 flex gap-2 z-10">
                {/* Bouton Stop */}
                <button
                  onClick={() => {
                    cleanupSketch();
                    setShowCanvas(false);
                  }}
                  className="bg-red-500 hover:bg-red-600 text-white rounded-full w-10 h-10 flex items-center justify-center shadow-md"
                  title="Fermer"
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 24 24" 
                    fill="currentColor" 
                    className="w-5 h-5"
                  >
                    <path d="M6 6h12v12H6z"/>
                  </svg>
                </button>
                
                {/* Bouton de relance */}
                <button
                  onClick={runSketch}
                  className="bg-green-500 hover:bg-green-600 text-white rounded-full w-10 h-10 flex items-center justify-center shadow-md"
                  title="Relancer le sketch"
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 24 24" 
                    fill="currentColor" 
                    className="w-5 h-5"
                  >
                    <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Assets Manager Modal */}
      <Dialog open={showAssetsManager} onOpenChange={setShowAssetsManager}>
        <DialogContent 
          className="sm:max-w-[800px] max-h-[80vh] overflow-y-auto"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Gestionnaire d'Assets</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            <AssetManager workdir={workdir} isSimplified={true} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EditorHalf; 